import {
  RequirementWorkflowType,
  WorkerRequirementDomain,
  WorkerRequirementStatus,
} from "@clipboard-health/contract-worker-app-bff";
import {
  BottomSheet,
  BottomSheetIllustrationContent,
  Text,
  useModalState,
} from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack, ThemeProvider } from "@mui/material";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { useGetPreference } from "@src/appV2/redesign/utils/useGetPreference";
import { useSetPreference } from "@src/appV2/redesign/utils/useSetPreference";
import { useWorkerQualifications } from "@src/appV2/Worker/useWorkerQualifications";
import { useCallback, useEffect, useMemo } from "react";

import {
  requirementRelationship,
  useGetWorkerRequirements,
} from "../Documents/api/useGetWorkerRequirements";
import { isAutoShiftUnassignmentWithinValidityWindow } from "../Documents/Details/Workflows/Checkr/isAutoShiftUnassignmentWithinValidityWindow";
import { useGetCheckrApplicationStatus } from "../Documents/Details/Workflows/Checkr/useGetCheckrApplicationStatus";
import { isCheckrApplicationStatusPending } from "../Documents/Details/Workflows/Checkr/utils";
import { useGetRunBackgroundCheckOnBookingConfiguration } from "./useGetRunBackgroundCheckOnBookingConfiguration";

const HAS_SEEN_BACKGROUND_CHECK_SHIFT_UNASSIGNMENT_NOTICE_KEY =
  "has-seen-background-check-shift-unassignment-notice";

export function BackgroundCheckShiftUnassignmentNotice() {
  const { data: checkrApplicationStatus } = useGetCheckrApplicationStatus();
  const config = useGetRunBackgroundCheckOnBookingConfiguration();
  const modalState = useModalState();

  const { data: haveSeenNoticeForUnassignmentTimestamp, refetch: refetchHasSeenNotice } =
    useGetPreference(HAS_SEEN_BACKGROUND_CHECK_SHIFT_UNASSIGNMENT_NOTICE_KEY);

  const { mutate: setHasSeenNotice } = useSetPreference({
    onSuccess: async () => {
      // To reload to get the latest value after updating the preference
      await refetchHasSeenNotice();
    },
  });

  const { data: workerQualifications } = useWorkerQualifications();
  const qualificationNames = useMemo(
    () => workerQualifications?.map((q) => q.attributes.name) ?? [],
    [workerQualifications]
  );

  const { data: workerRequirements } = useGetWorkerRequirements(
    {
      filter: {
        qualifications: qualificationNames,
        domains: [WorkerRequirementDomain.FLEX],
      },
      include: [...requirementRelationship],
    },
    {
      enabled: qualificationNames.length > 0,
    }
  );

  const checkrBackgroundCheckRequirement = useMemo(
    () =>
      workerRequirements?.find(
        (requirement) =>
          requirement.requirement?.attributes.requirementWorkflowType ===
          RequirementWorkflowType.CHECKR_BACKGROUND_CHECK
      ),
    [workerRequirements]
  );

  const autoShiftUnassignmentAt = checkrApplicationStatus?.data?.autoShiftUnassignmentAt;
  const reportStatus = checkrApplicationStatus?.data?.reportStatus;
  const requirementStatus = checkrBackgroundCheckRequirement?.status;

  const hasSeenNotice = haveSeenNoticeForUnassignmentTimestamp === autoShiftUnassignmentAt;

  const shouldShowBottomSheet =
    isDefined(autoShiftUnassignmentAt) &&
    isAutoShiftUnassignmentWithinValidityWindow({
      autoShiftUnassignmentAt,
      autoShiftUnassignmentValidityWindowInDays: config.autoShiftUnassignmentValidityWindowInDays,
    }) &&
    !hasSeenNotice &&
    ((isDefined(checkrApplicationStatus?.data) &&
      isCheckrApplicationStatusPending(checkrApplicationStatus.data)) ||
      requirementStatus === WorkerRequirementStatus.IN_REVIEW);

  const handleDismiss = useCallback(() => {
    setHasSeenNotice({
      key: HAS_SEEN_BACKGROUND_CHECK_SHIFT_UNASSIGNMENT_NOTICE_KEY,

      // autoShiftUnassignmentAt should always be defined if the bottom sheet is shown
      // This optional chaining is to satisfy the type checker
      value: autoShiftUnassignmentAt ?? "true",
    });
    modalState.closeModal();
  }, [setHasSeenNotice, modalState, autoShiftUnassignmentAt]);

  useEffect(() => {
    if (shouldShowBottomSheet) {
      modalState.openModal();
    } else {
      modalState.closeModal();
    }
  }, [shouldShowBottomSheet, modalState]);

  useLogEffect(
    APP_V2_APP_EVENTS.DELAYED_BACKGROUND_CHECK_NOTICE_SHOWN,
    {
      autoShiftUnassignmentAt,
      reportStatus,
      requirementStatus,
      hasSeenNotice,
    },
    {
      enabled: shouldShowBottomSheet,
    }
  );

  if (!shouldShowBottomSheet) {
    return null;
  }

  return (
    <ThemeProvider theme={getTheme()}>
      <BottomSheet
        canDismiss
        modalState={modalState}
        header={<BottomSheetHeader onClose={handleDismiss} />}
        footer={
          <DialogFooter orientation="vertical">
            <LoadingButton
              fullWidth
              variant="contained"
              size="large"
              isLoading={false}
              onClick={handleDismiss}
            >
              Continue
            </LoadingButton>
          </DialogFooter>
        }
      >
        <BottomSheetIllustrationContent
          illustrationType="warning"
          title="Your background check is still processing"
          description={
            <Stack direction="column" gap={4}>
              <Text>
                Your background check is taking longer than usual to complete. Because you had a
                shift starting within the next 24 hours, you were removed from that shift.
              </Text>

              <Text>
                You can continue booking shifts that start more than 24 hours from now while your
                background check is processing.
              </Text>
            </Stack>
          }
        />
      </BottomSheet>
    </ThemeProvider>
  );
}
